import React, { useState, useEffect, useRef } from "react";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";

import {
  Container,
  Row,
  Col,
  Card,
  ProgressBar,
  Button,
  Form,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  ChevronLeft,
  CheckCircleFill,
  PersonFill,
  EnvelopeFill,
  TelephoneFill,
  ArrowRight,
  Trophy,
  GeoAltFill,
} from "react-bootstrap-icons";
import Loader from "../../components/common/Loader";
import SubmissionLoader from "../../components/common/SubmissionLoader";
import AlertMessage from "../../components/common/Alert";
import api from "../../services/api";

const AssessmentPage = () => {
  const [step, setStep] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [studentInfo, setStudentInfo] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
  });
  const [allQuestions, setAllQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [studentId, setStudentId] = useState(null);
  const [phoneError, setPhoneError] = useState("");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [direction, setDirection] = useState("next");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const questionRef = useRef(null);
  const isYesAnswer = (value) => {
    if (!value) return false;
    const normalizedValue = String(value).trim().toLowerCase();
    return (
      normalizedValue === "yes" ||
      normalizedValue === "true" ||
      normalizedValue.startsWith("yes_")
    );
  };

  const colors = {
    background: "#032530",
    cardBg: "#0b2c36",
    text: "#FFFFFF",
    accent: "#ff8214",
    accentHover: "#ff9b44",
    border: "#1e273d",
    progressBg: "#0b2c36",
    success: "#018744",
    warning: "#ffaa00",
    danger: "#ff4757",
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  useEffect(() => {
    if (allQuestions.length > 0) {
      const filtered = allQuestions
        .map((question) => {
          const q1Answer = answers["q1"]?.answer;
          const q3Answer = answers["q3"]?.answer;
          const q15Answer = answers["q15"]?.answer;
          const q17Answer = answers["q17"]?.answer;
          const q18Answer = answers["q18"]?.answer;
          const q22Answer = answers["q22"]?.answer;

          let updatedQuestion = { ...question };

          switch (question.questionId) {
            case "q2":
              if (q1Answer === "pg") {
                updatedQuestion.options = question.options.filter(
                  (option) =>
                    option.value === "masters" ||
                    option.value === "higher_diploma" ||
                    option.value === "pre_master",
                );
                return updatedQuestion;
              }
              if (q1Answer === "ug") {
                updatedQuestion.options = question.options.filter(
                  (option) =>
                    option.value === "undergraduate" ||
                    option.value === "foundation" ||
                    option.value === "top_up_year",
                );
                return updatedQuestion;
              }

              return null;

            case "q5":
              return q3Answer !== "within_6_months" ? updatedQuestion : null;

            case "q16":
              if (q15Answer && q15Answer !== "no_travel") {
                return updatedQuestion;
              }
              return null;

            case "q17":
              if (
                q15Answer &&
                q15Answer !== "no_travel" &&
                q15Answer !== "other_travel"
              ) {
                return updatedQuestion;
              }
              return null;

            case "q18":
              if (
                q15Answer &&
                q15Answer !== "no_travel" &&
                q15Answer !== "other_travel" &&
                q17Answer &&
                q17Answer !== "never_travelled_uk"
              ) {
                return updatedQuestion;
              }
              return null;

            case "q19":
              if (
                q15Answer &&
                q15Answer !== "no_travel" &&
                q15Answer !== "other_travel" &&
                q17Answer &&
                q17Answer !== "never_travelled_uk" &&
                q18Answer &&
                (q18Answer === "currently_studying_masters" ||
                  q18Answer === "currently_studying_undergraduate")
              ) {
                return updatedQuestion;
              }
              return null;

            case "q20":
              return updatedQuestion;

            case "q21":
              return updatedQuestion;

            case "q23":
              return isYesAnswer(q22Answer) ? updatedQuestion : null;

            default:
              return updatedQuestion;
          }
        })
        .filter(Boolean)
        .sort((a, b) => a.order - b.order);

      setFilteredQuestions(filtered);

      if (currentQuestionIndex >= filtered.length) {
        setCurrentQuestionIndex(Math.max(0, filtered.length - 1));
      }
    }
  }, [allQuestions, answers, currentQuestionIndex]);

  const fetchQuestions = async () => {
    try {
      const response = await api.get("/questions");
      if (response.data.success) {
        const sortedQuestions = response.data.questions.sort(
          (a, b) => a.order - b.order,
        );
        setAllQuestions(sortedQuestions);
      }
    } catch (err) {
      setError("Failed to load questions. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const validatePhoneNumber = (phone) => {
    if (!phone || phone.length < 10) {
      return "Please enter a valid phone number";
    }
    return "";
  };

  const handleStudentInfoSubmit = async (e) => {
    e.preventDefault();

    const cleanPhone = studentInfo.phone.replace(/\s+/g, "");

    const phoneError = validatePhoneNumber(cleanPhone);
    if (phoneError) {
      setPhoneError(phoneError);
      return;
    }

    const response = await api.post("/students", {
      ...studentInfo,
      phone: cleanPhone,
    });

    if (response.data.success) {
      setStudentId(response.data.student._id);
      setStep(1);
    }
  };

  const handleAnswer = (questionId, value, points, optionIndex) => {
    const newAnswers = {
      ...answers,
      [questionId]: { answer: value, points, selectedIndex: optionIndex },
    };

    if (questionId === "q1") {
      if (answers["q2"]) {
        delete newAnswers["q2"];
      }
    }

    if (questionId === "q15") {
      if (value === "no_travel") {
        const answersToClear = ["q16", "q17", "q18", "q19"];
        answersToClear.forEach((qId) => {
          if (newAnswers[qId]) {
            delete newAnswers[qId];
          }
        });
      } else if (value === "other_travel") {
        const answersToClear = ["q17", "q18", "q19"];
        answersToClear.forEach((qId) => {
          if (newAnswers[qId]) {
            delete newAnswers[qId];
          }
        });
      } else if (value === "uk_eu_usa_canada_travel") {
      }
    }

    if (questionId === "q17") {
      if (value === "never_travelled_uk") {
        const answersToClear = ["q18", "q19"];
        answersToClear.forEach((qId) => {
          if (newAnswers[qId]) {
            delete newAnswers[qId];
          }
        });
      } else if (value === "negative_history") {
        const answersToClear = ["q18", "q19"];
        answersToClear.forEach((qId) => {
          if (newAnswers[qId]) {
            delete newAnswers[qId];
          }
        });
      }
    }

    if (questionId === "q18") {
      if (value === "never_studied_uk" || value === "degree_completed_psw") {
        if (newAnswers["q19"]) {
          delete newAnswers["q19"];
        }
      }
    }

    if (questionId === "q22" && !isYesAnswer(value)) {
      if (newAnswers["q23"]) {
        delete newAnswers["q23"];
      }
    }

    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQuestionIndex < filteredQuestions.length - 1) {
      setDirection("next");
      setIsTransitioning(true);

      setTimeout(() => {
        setCurrentQuestionIndex((prev) => prev + 1);
        setIsTransitioning(false);
      }, 300);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setDirection("prev");
      setIsTransitioning(true);

      setTimeout(() => {
        setCurrentQuestionIndex((prev) => prev - 1);
        setIsTransitioning(false);
      }, 300);
    }
  };

  const getCurrentQuestion = () => {
    return filteredQuestions[currentQuestionIndex];
  };

  const calculateScore = () => {
    let totalScore = 0;
    Object.entries(answers).forEach(([questionId, answer]) => {
      totalScore += answer.points || 0;
    });
    return totalScore;
  };

  const getProbabilityLevel = (score) => {
    let maxPossibleScore = 0;
    Object.values(answers).forEach((answer) => {
      if (answer.points > 0) maxPossibleScore += 5;
    });

    const percentage =
      maxPossibleScore > 0 ? (score / maxPossibleScore) * 100 : 0;

    if (percentage >= 80)
      return {
        level: "High",
        color: colors.success,
        percentage: "80-100%",
        icon: "🏆",
      };
    if (percentage >= 50)
      return {
        level: "Medium",
        color: colors.warning,
        percentage: "50-79%",
        icon: "📊",
      };
    return {
      level: "Low",
      color: colors.danger,
      percentage: "0-49%",
      icon: "🔍",
    };
  };

  const handleSubmitAssessment = async () => {
    try {
      setIsSubmitting(true);
      const totalScore = calculateScore();
      const probability = getProbabilityLevel(totalScore);

      const response = await api.post("/students/assessment", {
        studentId,
        answers: Object.entries(answers).map(([questionId, answer]) => ({
          questionId,
          answer: answer.answer,
          points: answer.points,
        })),
        totalScore,
        probabilityLevel: probability.level,
      });

      if (response.data.success) {
        navigate("/results", {
          state: {
            totalScore,
            probability,
            studentId,
            studentInfo: studentInfo,
            totalQuestions: allQuestions.length,
            answeredQuestions: Object.keys(answers).length,
            filteredQuestionsCount: filteredQuestions.length,
          },
        });
      }
    } catch (err) {
      setIsSubmitting(false);
      setError("Failed to submit assessment. Please try again.");
    }
  };

  const progressPercentage =
    step === 0
      ? 0
      : ((currentQuestionIndex + 1) / filteredQuestions.length) * 100;

  if (loading) return <Loader />;

  return (
    <div
      style={{
        backgroundColor: colors.background,
        minHeight: "100vh",
        paddingTop: "2rem",
        paddingBottom: "2rem",
      }}
    >
      <SubmissionLoader
        show={isSubmitting}
        message="Processing your assessment..."
      />
      <Container>
        <Row className="justify-content-center">
          <Col lg={8} xl={7}>
            {/* Header */}
            <div className="text-center mb-4">
              <h1
                style={{
                  color: "white",
                  fontWeight: "700",
                  marginBottom: "0.75rem",
                  fontSize: "2.25rem",
                }}
              >
                Visa Probability Assessment
              </h1>
              <p
                style={{
                  color: colors.text,
                  opacity: 0.8,
                  fontSize: "1rem",
                  maxWidth: "500px",
                  margin: "0 auto",
                }}
              >
                Complete this assessment to get your personalized visa success
                probability
              </p>
            </div>

            <Card
              className="border-0"
              style={{
                backgroundColor: "#13181f",
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: `0 8px 30px rgba(0, 0, 0, 0.3)`,
                border: `1px solid ${colors.border}`,
              }}
            >
              {/* Progress Section */}
              <div
                style={{
                  backgroundColor: "#13181f",
                  padding: "1.25rem 1.5rem",
                  borderBottom: `1px solid ${colors.border}`,
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div
                    style={{
                      color: colors.text,
                      fontWeight: "600",
                      fontSize: "0.95rem",
                    }}
                  >
                    {step === 0
                      ? "Personal Information"
                      : `Question ${currentQuestionIndex + 1} of ${
                          filteredQuestions.length
                        }`}
                  </div>
                  <div
                    style={{
                      color: colors.accent,
                      fontWeight: "700",
                      fontSize: "1rem",
                    }}
                  >
                    {Math.round(progressPercentage)}% Complete
                  </div>
                </div>
                <ProgressBar
                  now={progressPercentage}
                  style={{
                    height: "8px",
                    backgroundColor: colors.background,
                    borderRadius: "4px",
                    overflow: "hidden",
                    transition: "width 0.5s ease",
                  }}
                  className="mb-2"
                >
                  <ProgressBar
                    animated
                    style={{
                      backgroundColor: colors.accent,
                      background: `linear-gradient(90deg, ${colors.accent}, ${colors.accentHover})`,
                      transition: "width 0.5s ease",
                    }}
                  />
                </ProgressBar>
                <div
                  style={{
                    color: colors.text,
                    opacity: 0.7,
                    fontSize: "0.85rem",
                    textAlign: "right",
                  }}
                >
                  {step === 0
                    ? "Step 1/2"
                    : `${currentQuestionIndex + 1} of ${
                        filteredQuestions.length
                      } questions answered`}
                </div>
              </div>

              {/* Card Body */}
              <Card.Body style={{ padding: "1.75rem", minHeight: "400px" }}>
                {error && (
                  <AlertMessage
                    variant="danger"
                    message={error}
                    onClose={() => setError(null)}
                  />
                )}

                {step === 0 ? (
                  <Form onSubmit={handleStudentInfoSubmit}>
                    <div className="mb-3">
                      <h5
                        style={{
                          color: colors.text,
                          marginBottom: "1rem",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "1.1rem",
                        }}
                      >
                        <PersonFill
                          size={18}
                          style={{ color: colors.accent }}
                        />
                        Tell us about yourself
                      </h5>
                      <p
                        style={{
                          color: colors.text,
                          opacity: 0.8,
                          marginBottom: "1.5rem",
                          fontSize: "0.95rem",
                        }}
                      >
                        We'll use this information to personalize your
                        assessment results
                      </p>
                    </div>

                    <Form.Group className="mb-3">
                      <div className="d-flex align-items-center mb-2">
                        <PersonFill
                          size={16}
                          style={{
                            color: colors.accent,
                            marginRight: "8px",
                            opacity: 0.7,
                          }}
                        />
                        <Form.Label
                          style={{
                            color: colors.text,
                            fontWeight: "500",
                            fontSize: "0.95rem",
                            marginBottom: 0,
                          }}
                        >
                          Full Name
                        </Form.Label>
                      </div>
                      <Form.Control
                        type="text"
                        value={studentInfo.name}
                        onChange={(e) =>
                          setStudentInfo({
                            ...studentInfo,
                            name: e.target.value,
                          })
                        }
                        required
                        style={{
                          backgroundColor: colors.background,
                          border: `1px solid ${colors.border}`,
                          color: colors.text,
                          padding: "10px 14px",
                          borderRadius: "8px",
                          fontSize: "0.95rem",
                        }}
                        placeholder="Enter your full name"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <div className="d-flex align-items-center mb-2">
                        <EnvelopeFill
                          size={16}
                          style={{
                            color: colors.accent,
                            marginRight: "8px",
                            opacity: 0.7,
                          }}
                        />
                        <Form.Label
                          style={{
                            color: colors.text,
                            fontWeight: "500",
                            fontSize: "0.95rem",
                            marginBottom: 0,
                          }}
                        >
                          Email Address
                        </Form.Label>
                      </div>
                      <Form.Control
                        type="email"
                        value={studentInfo.email}
                        onChange={(e) =>
                          setStudentInfo({
                            ...studentInfo,
                            email: e.target.value,
                          })
                        }
                        required
                        style={{
                          backgroundColor: colors.background,
                          border: `1px solid ${colors.border}`,
                          color: colors.text,
                          padding: "10px 14px",
                          borderRadius: "8px",
                          fontSize: "0.95rem",
                        }}
                        placeholder="your.email@example.com"
                      />
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <div className="d-flex align-items-center mb-2">
                        <TelephoneFill
                          size={16}
                          style={{
                            color: colors.accent,
                            marginRight: "8px",
                            opacity: 0.7,
                          }}
                        />
                        <Form.Label
                          style={{
                            color: colors.text,
                            fontWeight: "500",
                            fontSize: "0.95rem",
                            marginBottom: 0,
                          }}
                        >
                          Phone Number
                        </Form.Label>
                      </div>

                      <PhoneInput
                        country={"pk"}
                        value={studentInfo.phone}
                        onChange={(phone) =>
                          setStudentInfo({
                            ...studentInfo,
                            phone: phone.replace(/\s+/g, ""),
                          })
                        }
                        enableSearch
                        countryCodeEditable={false}
                        disableFormatting={true}
                        inputStyle={{
                          width: "100%",
                          backgroundColor: colors.background,
                          border: `1px solid ${colors.border}`,
                          color: colors.text,
                          borderRadius: "8px",
                          height: "42px",
                        }}
                        buttonStyle={{
                          backgroundColor: colors.background,
                          border: `1px solid ${colors.border}`,
                          borderRadius: "8px 0 0 8px",
                        }}
                        dropdownStyle={{
                          backgroundColor: "#13181f",
                          color: "#fff",
                        }}
                        placeholder="Enter phone number"
                      />

                      <Form.Text
                        style={{
                          color: colors.success,
                          fontSize: "0.85rem",
                          marginTop: "6px",
                          display: "block",
                        }}
                      >
                        ✓ Enter number with country code
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <div className="d-flex align-items-center mb-2">
                        <GeoAltFill
                          size={16}
                          style={{
                            color: colors.accent,
                            marginRight: "8px",
                            opacity: 0.7,
                          }}
                        />
                        <Form.Label
                          style={{
                            color: colors.text,
                            fontWeight: "500",
                            fontSize: "0.95rem",
                            marginBottom: 0,
                          }}
                        >
                          City
                        </Form.Label>
                      </div>
                      <Form.Control
                        type="text"
                        value={studentInfo.city}
                        onChange={(e) =>
                          setStudentInfo({
                            ...studentInfo,
                            city: e.target.value,
                          })
                        }
                        required
                        style={{
                          backgroundColor: colors.background,
                          border: `1px solid ${colors.border}`,
                          color: colors.text,
                          padding: "10px 14px",
                          borderRadius: "8px",
                          fontSize: "0.95rem",
                        }}
                        placeholder="Enter your city"
                      />
                    </Form.Group>

                    <Button
                      type="submit"
                      style={{
                        backgroundColor: colors.accent,
                        borderColor: colors.accent,
                        color: colors.background,
                        borderRadius: "10px",
                        padding: "12px 16px",
                        fontSize: "1rem",
                        fontWeight: "600",
                        width: "100%",
                        transition: "all 0.3s ease",
                        boxShadow: `0 4px 15px rgba(255, 130, 20, 0.3)`,
                        marginTop: "0.5rem",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                          colors.accentHover;
                        e.currentTarget.style.borderColor = colors.accentHover;
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = `0 6px 20px rgba(255, 155, 68, 0.4)`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = colors.accent;
                        e.currentTarget.style.borderColor = colors.accent;
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = `0 4px 15px rgba(255, 130, 20, 0.3)`;
                      }}
                    >
                      Start Assessment
                      <ChevronRight size={18} className="ms-2" />
                    </Button>
                  </Form>
                ) : filteredQuestions.length > 0 ? (
                  <div
                    ref={questionRef}
                    style={{
                      transform: isTransitioning
                        ? direction === "next"
                          ? "translateX(-20px)"
                          : "translateX(20px)"
                        : "translateX(0)",
                      opacity: isTransitioning ? 0 : 1,
                      transition: "transform 0.3s ease, opacity 0.3s ease",
                      height: "100%",
                    }}
                  >
                    {/* Question Header */}
                    <div className="mb-3">
                      <div
                        style={{
                          color: colors.accent,
                          fontWeight: "600",
                          fontSize: "0.85rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: "0.5rem",
                        }}
                      >
                        Question {currentQuestionIndex + 1}
                        {getCurrentQuestion()?.category && (
                          <span style={{ marginLeft: "10px", opacity: 0.7 }}>
                            ({getCurrentQuestion()?.category})
                          </span>
                        )}
                      </div>
                      <h4
                        style={{
                          color: colors.text,
                          fontWeight: "600",
                          lineHeight: "1.4",
                          fontSize: "1.25rem",
                          animation: !isTransitioning
                            ? "fadeInUp 0.5s ease"
                            : "none",
                        }}
                      >
                        {getCurrentQuestion()?.questionText}
                      </h4>
                    </div>

                    {/* Options */}
                    <div className="mb-4">
                      {getCurrentQuestion()?.options.map((option, index) => {
                        const selectedIndex =
                          answers[getCurrentQuestion()?.questionId]
                            ?.selectedIndex;
                        const isSelected = selectedIndex === index;

                        return (
                          <div
                            key={index}
                            onClick={() =>
                              handleAnswer(
                                getCurrentQuestion()?.questionId,
                                option.value,
                                option.points,
                                index,
                              )
                            }
                            style={{
                              backgroundColor: isSelected
                                ? `${colors.accent}15`
                                : colors.background,
                              border: `2px solid ${isSelected ? colors.accent : colors.border}`,
                              borderRadius: "10px",
                              padding: "14px 16px",
                              marginBottom: "10px",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              position: "relative",
                              transform: isSelected
                                ? "scale(1.02)"
                                : "scale(1)",
                              animation: !isTransitioning
                                ? `fadeIn 0.4s ease ${index * 0.1}s backwards`
                                : "none",
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.transform = "scale(1.02)";
                                e.currentTarget.style.borderColor = `${colors.accent}80`;
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.transform = "scale(1)";
                                e.currentTarget.style.borderColor =
                                  colors.border;
                              }
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <div
                                style={{
                                  width: "22px",
                                  height: "22px",
                                  borderRadius: "50%",
                                  border: `2px solid ${isSelected ? colors.accent : colors.border}`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  marginRight: "12px",
                                  flexShrink: 0,
                                  backgroundColor: isSelected
                                    ? colors.accent
                                    : "transparent",
                                  transition: "all 0.2s ease",
                                }}
                              >
                                {isSelected && (
                                  <CheckCircleFill
                                    size={10}
                                    style={{ color: colors.background }}
                                  />
                                )}
                              </div>
                              <div
                                style={{
                                  color: colors.text,
                                  fontWeight: isSelected ? "600" : "500",
                                  fontSize: "1rem",
                                  lineHeight: "1.4",
                                }}
                              >
                                {option.text}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Navigation Buttons */}
                    <div
                      className="d-flex justify-content-between pt-2"
                      style={{
                        animation: !isTransitioning
                          ? "fadeIn 0.5s ease 0.3s backwards"
                          : "none",
                      }}
                    >
                      <Button
                        variant="outline"
                        onClick={handlePrevious}
                        disabled={currentQuestionIndex === 0 || isTransitioning}
                        style={{
                          border: `1px solid ${colors.border}`,
                          color: colors.text,
                          borderRadius: "8px",
                          padding: "10px 20px",
                          fontSize: "0.9rem",
                          opacity: currentQuestionIndex === 0 ? 0.5 : 1,
                          backgroundColor: "transparent",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (currentQuestionIndex !== 0) {
                            e.currentTarget.style.borderColor = colors.accent;
                            e.currentTarget.style.color = colors.accent;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentQuestionIndex !== 0) {
                            e.currentTarget.style.borderColor = colors.border;
                            e.currentTarget.style.color = colors.text;
                          }
                        }}
                      >
                        <ChevronLeft size={14} />
                        Previous
                      </Button>

                      {currentQuestionIndex === filteredQuestions.length - 1 ? (
                        <Button
                          onClick={handleSubmitAssessment}
                          disabled={
                            !answers[getCurrentQuestion()?.questionId] ||
                            isTransitioning
                          }
                          style={{
                            backgroundColor: colors.accent,
                            borderColor: colors.accent,
                            color: colors.background,
                            borderRadius: "8px",
                            padding: "10px 24px",
                            fontSize: "0.9rem",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            opacity: !answers[getCurrentQuestion()?.questionId]
                              ? 0.6
                              : 1,
                            transition: "all 0.3s ease",
                            boxShadow: !answers[
                              getCurrentQuestion()?.questionId
                            ]
                              ? "none"
                              : `0 4px 15px rgba(255, 130, 20, 0.3)`,
                            transform: !answers[
                              getCurrentQuestion()?.questionId
                            ]
                              ? "scale(1)"
                              : "scale(1)",
                          }}
                          onMouseEnter={(e) => {
                            if (answers[getCurrentQuestion()?.questionId]) {
                              e.currentTarget.style.backgroundColor =
                                colors.accentHover;
                              e.currentTarget.style.borderColor =
                                colors.accentHover;
                              e.currentTarget.style.transform = "scale(1.05)";
                              e.currentTarget.style.boxShadow = `0 6px 20px rgba(255, 155, 68, 0.4)`;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (answers[getCurrentQuestion()?.questionId]) {
                              e.currentTarget.style.backgroundColor =
                                colors.accent;
                              e.currentTarget.style.borderColor = colors.accent;
                              e.currentTarget.style.transform = "scale(1)";
                              e.currentTarget.style.boxShadow = `0 4px 15px rgba(255, 130, 20, 0.3)`;
                            }
                          }}
                        >
                          <Trophy size={16} />
                          Submit Assessment
                        </Button>
                      ) : (
                        <Button
                          onClick={handleNext}
                          disabled={
                            !answers[getCurrentQuestion()?.questionId] ||
                            isTransitioning
                          }
                          style={{
                            backgroundColor: colors.accent,
                            borderColor: colors.accent,
                            color: colors.background,
                            borderRadius: "8px",
                            padding: "10px 24px",
                            fontSize: "0.9rem",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            opacity: !answers[getCurrentQuestion()?.questionId]
                              ? 0.6
                              : 1,
                            transition: "all 0.3s ease",
                            boxShadow: !answers[
                              getCurrentQuestion()?.questionId
                            ]
                              ? "none"
                              : `0 4px 15px rgba(255, 130, 20, 0.3)`,
                            transform: !answers[
                              getCurrentQuestion()?.questionId
                            ]
                              ? "scale(1)"
                              : "scale(1)",
                          }}
                          onMouseEnter={(e) => {
                            if (answers[getCurrentQuestion()?.questionId]) {
                              e.currentTarget.style.backgroundColor =
                                colors.accentHover;
                              e.currentTarget.style.borderColor =
                                colors.accentHover;
                              e.currentTarget.style.transform = "scale(1.05)";
                              e.currentTarget.style.boxShadow = `0 6px 20px rgba(255, 155, 68, 0.4)`;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (answers[getCurrentQuestion()?.questionId]) {
                              e.currentTarget.style.backgroundColor =
                                colors.accent;
                              e.currentTarget.style.borderColor = colors.accent;
                              e.currentTarget.style.transform = "scale(1)";
                              e.currentTarget.style.boxShadow = `0 4px 15px rgba(255, 130, 20, 0.3)`;
                            }
                          }}
                        >
                          Next Question
                          <ArrowRight size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <p style={{ color: colors.text, fontSize: "1.1rem" }}>
                      Loading questions...
                    </p>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Footer Note */}
            <div
              style={{
                textAlign: "center",
                marginTop: "1.5rem",
                color: colors.text,
                opacity: 0.6,
                fontSize: "0.85rem",
              }}
            >
              <p className="mb-1">
                Your information is secure and will only be used for assessment
                purposes
              </p>
              <p className="mb-0">
                Questions are filtered based on your answers to previous
                questions
              </p>
            </div>
          </Col>
        </Row>
      </Container>

      <style jsx="true">{`
        ::-webkit-scrollbar {
          width: 6px;
        }

        ::-webkit-scrollbar-track {
          background: ${colors.background};
        }

        ::-webkit-scrollbar-thumb {
          background: ${colors.accent};
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: ${colors.accentHover};
        }

        .form-control:focus {
          background-color: ${colors.background};
          border-color: ${colors.accent};
          color: ${colors.text};
          box-shadow: 0 0 0 0.2rem rgba(255, 130, 20, 0.25);
        }

        .btn:disabled {
          cursor: not-allowed;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default AssessmentPage;
